/**
 * Helper for generating compact SOS Paper Token and URL
 * Scheme B format: `p=1.<country>.<insurer>.<ref>.<theme>.<origin>.<lang>`
 */

export const INSURER_FULL_TO_SHORT = {
  aia: 'aia',
  pru: 'pru',
  manulife: 'ml',
  axa: 'axa',
  fwd: 'fwd',
  sunlife: 'sun',
  bupa: 'bupa',
  chinalife: 'cli',
  pingan: 'pa',
  zurich: 'zur',
  bluecross: 'bc',
  chubb: 'cb',
  bocg: 'boc',
  bowtie: 'bow',
  hsbc: 'hsbc',
  boclife: 'bocl',
  ctflife: 'ctf',
  yflife: 'yf',
  cigna: 'cig',
  taiping: 'tp',
  generali: 'gen',
  hangseng: 'hs',
  starr: 'str',
  aig: 'aig',
  msig: 'msig'
};

// Recommended theme mapping when an adviser selects an insurer
export const INSURER_RECOMMENDED_THEME = {
  aia: 'rd',
  pru: 'rd',
  manulife: 'bl',
  axa: 'nv',
  fwd: 'or',
  sunlife: 'or',
  bupa: 'bl',
  chinalife: 'gr',
  pingan: 'or',
  zurich: 'nv',
  bluecross: 'bl',
  chubb: 'pr',
  bocg: 'rd',
  bowtie: 'bl',
  hsbc: 'rd',
  boclife: 'rd',
  ctflife: 'nv',
  yflife: 'nv',
  cigna: 'bl',
  taiping: 'bl',
  generali: 'rd',
  hangseng: 'gr',
  starr: 'rd',
  aig: 'nv',
  msig: 'nv'
};

export const THEME_PRESETS = [
  {
    id: 'bl',
    name: '經典藍',
    color: '#2563eb',
    bgLight: '#eff6ff',
    border: '#bfdbfe'
  },
  {
    id: 'rd',
    name: '活力紅',
    color: '#dc2626',
    bgLight: '#fef2f2',
    border: '#fecaca'
  },
  {
    id: 'gr',
    name: '翡翠綠',
    color: '#059669',
    bgLight: '#ecfdf5',
    border: '#a7f3d0'
  },
  {
    id: 'nv',
    name: '尊貴深藍',
    color: '#1e3a8a',
    bgLight: '#f0f4ff',
    border: '#c7d2fe'
  },
  {
    id: 'or',
    name: '暖陽橘',
    color: '#ea580c',
    bgLight: '#fff7ed',
    border: '#fed7aa'
  },
  {
    id: 'pr',
    name: '摩登紫',
    color: '#7c3aed',
    bgLight: '#faf5ff',
    border: '#e9d5ff'
  },
  {
    id: 'pk',
    name: '優雅粉',
    color: '#db2777',
    bgLight: '#fdf2f8',
    border: '#fbcfe8'
  },
  {
    id: 'gp',
    name: '石墨灰',
    color: '#334155',
    bgLight: '#f8fafc',
    border: '#e2e8f0'
  }
];

/**
 * Compresses 24-char hex MongoDB ObjectId to 16-char Base64URL string.
 */
export function compressObjectId(hexId) {
  if (!hexId) return '';
  const trimmed = String(hexId).trim();
  if (trimmed.length !== 24 || !/^[0-9a-fA-F]{24}$/.test(trimmed)) {
    return trimmed;
  }

  try {
    const bytes = [];
    for (let i = 0; i < 24; i += 2) {
      bytes.push(parseInt(trimmed.slice(i, i + 2), 16));
    }
    let bin = '';
    for (let i = 0; i < bytes.length; i++) {
      bin += String.fromCharCode(bytes[i]);
    }

    const b64 = typeof btoa !== 'undefined' ? btoa(bin) : Buffer.from(bin, 'binary').toString('base64');

    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (err) {
    console.warn('[SOS Token] Failed to compress hexId:', trimmed, err);
    return trimmed;
  }
}

// Must match TRAVELKIT_TOKEN_VERSION in travelkit/src/utils/token.ts. Emitted bare
// (`1.`, not `v1.`) — the `v` said nothing the leading digit did not, and every
// character rides in a QR code and an SMS.
export const TRAVELKIT_TOKEN_VERSION = 1;

/**
 * Builds the compact `p` parameter string.
 * Format: `1.<Country>.<InsurerShort>.<CompressedRef>.<Theme>.<Origin>.<Lang>`
 * Example: `1.JP.ml.Wu6LNpRlsy_e2SCT.rd.cn.sc`
 *
 * The leading version lets the SOS page tell this layout from any future one,
 * since printed links cannot be recalled.
 *
 * Smart Tail Truncation:
 * - If origin is 'hk' and lang is 'tc' (default), both are omitted from the tail!
 *   e.g. `1.JP.ml.Wu6LNpRlsy_e2SCT.rd`
 * - If origin is 'cn' and lang is 'tc', lang is omitted:
 *   e.g. `1.JP.ml.Wu6LNpRlsy_e2SCT.rd.cn`
 * - If lang is not 'tc', both origin and lang are included:
 *   e.g. `1.JP.ml.Wu6LNpRlsy_e2SCT.rd.hk.en`
 *
 * travelkit's `scripts/check-travelkit-token.mjs` asserts this emits byte-identical
 * tokens to the SOS page's own encoder.
 *
 * @param {{ country?: string, insurer?: string, adviserId?: string, theme?: string, origin?: string, lang?: string }} params
 */
export function buildTravelKitToken({ country, insurer, adviserId, theme, origin, lang }) {
  const c = country ? country.toUpperCase() : 'JP';
  const i = insurer ? INSURER_FULL_TO_SHORT[insurer.toLowerCase()] || insurer.toLowerCase() : '_';
  const r = adviserId ? compressObjectId(adviserId) || '_' : '_';
  const t = theme ? theme.toLowerCase() : 'bl';
  const o = origin && origin.toLowerCase() !== 'hk' ? origin.toLowerCase() : '';
  const l = lang && lang.toLowerCase() !== 'tc' ? lang.toLowerCase() : '';

  const head = `${TRAVELKIT_TOKEN_VERSION}.${c}.${i}.${r}.${t}`;
  if (!o && !l) {
    return head;
  }
  if (o && !l) {
    return `${head}.${o}`;
  }
  return `${head}.${o || 'hk'}.${l}`;
}
